import {
  PropsWithChildren,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react'
import { formService } from '@oneblink/apps-react'
import { useLoadResourcesState } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'
import config from '../config'

type TFormsDefinitionContext = { forms: FormTypes.Form[]; isFetching: boolean }

const FormsDefinitionContext = createContext<TFormsDefinitionContext>({
  forms: [],
  isFetching: true,
})

export const useFormsDefinitions = function useFormsDefinitions() {
  return useContext(FormsDefinitionContext)
}

export function useToFormId(formIdOrSlug: string) {
  const { forms } = useFormsDefinitions()
  if (forms) {
    const formId = parseInt(formIdOrSlug)

    if (isNaN(formId)) {
      return forms.find((form) => form.slug === formIdOrSlug)?.id
    }

    return formId
  }
}

export function useToFormSlug(formIdOrSlug: string): string | undefined {
  const { forms } = useFormsDefinitions()
  if (forms) {
    const formId = parseInt(formIdOrSlug)

    if (isNaN(formId)) {
      return forms.find((form) => form.slug === formIdOrSlug)?.slug
    }
  }
}

export function getFormSlugOrId(form: FormTypes.Form) {
  return form.slug ? form.slug : form.id
}

const FetchFormsError = () => {
  return <span>There has been an error fetching forms.</span>
}
FetchFormsError.displayName = 'FetchFormsError'

export default function FormDefinitionsProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const fetchForms = useCallback(async (abortSignal: AbortSignal) => {
    return await formService.getForms(config.FORMS_APP_ID, abortSignal)
  }, [])

  const [forms, isFetching, fetchError] = useLoadResourcesState(fetchForms)

  const value = useMemo(() => ({ forms, isFetching }), [forms, isFetching])

  if (fetchError) {
    return <FetchFormsError />
  }

  return (
    <FormsDefinitionContext.Provider value={value}>
      {children}
    </FormsDefinitionContext.Provider>
  )
}
